<script lang="ts">
  /**
   * The provider filter. Its vocabulary is the exposure row's own `provider` field -
   * `sharepoint` / `google-drive` - *not* `FindingProviderSelect`'s `msgraph-v4` /
   * `google-drive-v1.0.0` connector ids. Absent means all providers.
   *
   * Single-select, laid out like the status select: "All providers" first, then each provider,
   * each with its count when `counts` is given.
   */
  import * as Select from "$ui/select";

  import { providerLabel, provisionalSuffix, PROVIDERS, type Provider } from "../exposedSensitiveDocuments";

  let {
    value = null,
    counts,
    provisional = false,
    onChange,
  }: {
    value?: Provider | null;
    counts?: readonly { provider: Provider; count: number }[];
    /** The rows are still loading, so each count is only the rows so far (#2265). */
    provisional?: boolean;
    onChange: (provider: Provider | null) => void;
  } = $props();

  const ALL = "all";
  const ALL_LABEL = "All providers";

  const countOf = (provider: Provider) => counts?.find((entry) => entry.provider === provider)?.count ?? 0;
  const total = $derived(counts?.reduce((sum, { count }) => sum + count, 0) ?? 0);
</script>

<!-- Unlabelled and the status select's width (spec 9.6); the trigger keeps an aria-label. -->
<div class="w-48">
  <Select.Root
    onSelectedChange={(selected) =>
      onChange(selected?.value === ALL ? null : ((selected?.value ?? null) as Provider | null))}
  >
    <Select.Trigger name="provider" aria-label="Provider">
      <Select.Value placeholder={value ? providerLabel(value) : ALL_LABEL} />
    </Select.Trigger>
    <Select.Content sameWidth={true}>
      <Select.Group>
        <Select.Item value={ALL} label={ALL_LABEL} isSelected={value === null}>
          <span class="flex-1">{ALL_LABEL}</span>
          {#if counts}
            <span class="text-xs tabular-nums text-gray-400" data-testid="provider-option-count"
              >{total}{provisionalSuffix(provisional)}</span
            >
          {/if}
        </Select.Item>
        <Select.Separator />
        {#each PROVIDERS as provider (provider)}
          <Select.Item value={provider} label={providerLabel(provider)} isSelected={value === provider}>
            <span class="flex-1">{providerLabel(provider)}</span>
            {#if counts}
              <span class="text-xs tabular-nums text-gray-400" data-testid="provider-option-count"
                >{countOf(provider)}{provisionalSuffix(provisional)}</span
              >
            {/if}
          </Select.Item>
        {/each}
      </Select.Group>
    </Select.Content>
  </Select.Root>
</div>
